import type {
  CalendarView,
  CommunityStatus,
  CommunitySubType,
  GroupEntity,
  PermissionLevel,
  Resource,
  ResourceColor,
  ResourceType,
  UserSubType,
  UserType,
} from './common';
import type { Membership } from './membership';
import type { SignatureEvidence } from './signing';

export interface UserPreferences {
  calendarDefaultView?: CalendarView;
  defaultView?: number;
  lastPeriod?: number;
  lastToolModeMap?: {
    defaults?: {
      StrokeColor?: {
        R: number;
        G: number;
        B: number;
        A: number;
      };
      StrokeThickness?: number;
      Opacity?: number;
    };
    name?: string;
    layoutMode?: string;
  }[];
  lastWidget?: number;
  signatureRequestLastLevel?: number;
  signatureLastLevel?: number;
  usersView?: number;
  groupsView?: number;
  sidebar?: boolean;
  tasksType?: 'author' | 'executor';
  tasksStatus?: 0 | 1;
  resourceColor?: number;
  upcomingEventsAttendance?: number;
  upcomingEventsCategories?: ResourceColor[];
}

export enum CommunityProfileType {
  EXECUTIVE_AND_BOARDROOM = 1,
  PROJECT_ROOM = 2,
  SIGNING_ROOM = 3,
}

export enum LoginInfoMode {
  DO_NOT_SEND = 0,
  SEND_WITH_PASSWORD = 1,
  SEND_WITHOUT_PASSWORD = 2,
}

export enum AuthenticationMethod {
  PASSWORD_ONLY = 0,
  SMS_CHALLENGE = 2,
  CERTIFICATE = 4,
  MOBILE_ID = 5,
  CONCATENATION = 6,
  OTP = 7,
  SMARTCARD = 8,
  EMAIL_CHALLENGE = 9,
  FINMA_SSO = 10,
  AZURE_AD_SSO = 11,
}

export interface Auth {
  userId: number;
  username: string;
  password: string;
  status: number;
  authenticationMethod: AuthenticationMethod;
  visibleFrom: number;
  expireDate: number;
  mobile: string;
  email: string;
  communityId: number;
  communityName: string;
  communityStatus: CommunityStatus;
  encryptionDigestAlgorithm: string;
  encryptionMacAlgorithm: string;
  encryptionSecretKeyAlgorithm: string;
  encryptionTransformationLong: string;
  encryptionTransformationShort: string;
  encryptionPasswordEncoder: string;
  encryptionCommunityKeyManager: string;
  encryptionServerKey: string;
}

export interface EiamProperties {
  email: string;
  firstName: string;
  lastName: string;
  admin: boolean;
  accounts: Auth[];
}

export interface SystemToken {
  accessToken: string;
  expiration: number;
  type: string;
  duration: number;
}

export interface UserSettings {
  access_token: string;
  communityExpirationDaysCount: number;
  guest: boolean;
  member: boolean;
  shortName: string;
  imageDefined: boolean;
  username: string;
  communityLoginName: string;
  signature: boolean;
  functions: Record<string, boolean>;
  permission: number;
  roomPermission: number;
  lastLoginDetails: Record<string, unknown>;
  billing: boolean;
  ai?: boolean;
  preferences: UserPreferences;
  samlUser?: EiamProperties;
  signatureImageDefault: boolean;
  conversationNotifications: boolean;
  signatureCustom: boolean;
  identificationURL: string;
  maxFileSize: number;
  testSignaturesExhausted: boolean;
  signatureEvidenceEidas: number[];
  signatureEvidenceZertes: number[];
  fileViewerLicense?: Nullable<string>;

  userId?: number;
  roomId?: number;
  displayName: string;
  subtype: UserSubType;
  language: string;
  lastLogin: number;
  authenticationMethod: AuthenticationMethod;
  rootUsersPermission: number;
  rootUsersId: number;
  rootRoomsPermission: number;
  rootRoomsId: number;
  rootGroupsPermission: number;
  rootGroupsId: number;
  communityPermission: PermissionLevel;
  signingRoomPermission: number;
  signingRoomId: number;
  communityId: number;
  communitySubtype: CommunitySubType;
  communityName: string;
  communityLogo: boolean;
  communityLogoUrl?: string;
  communityReadUploadPermission: boolean;
  communityReporting: boolean;
  communitySignature: boolean;
  communityExpireDate: number;
  communityTimeout: number;
  communityStatus: CommunityStatus;
  communityProfileId?: CommunityProfileType;
  defaultView: number;
  maps: boolean;
  homonyms: boolean;
  pagingSize: number;
  privasphere: boolean;
  guestCreation: boolean;
  signatureSeal: boolean;
  editorDeletePermission: boolean;
  recentActivities: number;
  swisscomSign?: string;
  showSigningRoom: boolean;
  communitySystemToken?: boolean;
  communityTrashBinLifetime?: number;
  authorSubtype?: number;
  communityOneTimeGuests?: boolean;
  systemToken?: boolean;

  decision?: number;
  decisionDate?: Nullable<number>;
  decisionComment?: Nullable<string>;
  option?: number;

  mustAcceptConditions?: boolean;
}

export interface AcceptConditionsResponse extends UserSettings {
  mustChangePwd?: boolean;
  policyPwd?: {
    minLength: number;
    minQuality: number;
    policies: { type: string; enabled: boolean }[];
  };
  firstName?: string;
  lastName?: string;
}

export interface User extends Resource<ResourceType.USER> {
  dateOfBirth: number;
  membershipsGroups: GroupEntity[];
  username: string;
  lastName: string;
  firstName: string;
  password: string;
  authenticationMethod: AuthenticationMethod;
  language: string;
  email: string;
  lastLogin: number;
  lastPasswordChange: number;
  phoneHome: string;
  phoneWork: string;
  mobile: string;
  fax: string;
  company: string;
  department: string;
  position: string;
  address: string;
  zip: string;
  locality: string;
  region: string;
  country: string;
  showDateOfBirthInCalendar: boolean;
  roomId: number;
  roomPermission: number;
  roomSize: number;
  loginInfo: LoginInfoMode;
  imageSmall: string;
  imageDefined: boolean;
  guestAccessPropagate: boolean;
  guestCreation: boolean;
  ldapObjectGuid: string;
  ldapCn: string;
  signature: number;
  conversationsDiskSpace: number;
  gender: number;
  defaultView: number;
  otpSharedKey: boolean;
  geolocationFilter?: string;
  geolocationSupport: number;
  createPersonalRoom: boolean;
  conditionsAccepted: number;
  signatureImage: string;
  signatureImageDefault: boolean;
  foreignAuthority: boolean;
  signatureCountry: string;
  signatureSeal: boolean;
  signingGuest: boolean;
  signatureEvidences: SignatureEvidence[];
  memberships?: Membership[];
  systemToken?: boolean;
  image?: string;
}

export interface UserPostBody {
  lastName: string;
  firstName: string;
  email: string;
  mobile: string;
  phoneHome: string;
  phoneWork: string;
  fax: string;
  address: string;
  zip: string;
  locality: string;
  company: string;
  department: string;
  position: string;
  language: string;
  username: string;
  authenticationMethod: AuthenticationMethod;
  geolocationFilter: string;
  password: string;
  signatureCountry: string;
  memberships: Pick<Membership, 'rid'>[];
  inheritSettings: boolean;
  type: UserType;
  subtype: UserSubType;
  country?: string;
  loginInfo?: LoginInfoMode;
  dateOfBirth?: number;
  expireDate?: string;
  visibleFrom?: string;
}
